import { durationUnitToDays } from '../medication-drug/utils';

import { EMPTY_RESULT } from '@/shared/constants';
import type { Order } from '@/shared/types';
import type { PastPrescriptionPanelTab } from '@/shared/types/prescription/prescription';
import type { VisitEncounter as Encounter, Visit } from '@/shared/types/prescription/visit';
import { format, isValid, parse, parseISO } from 'date-fns';

type EncounterTypeConfig = {
  uuid: string;
  display: string;
};

const getTabTitle = (tab: PastPrescriptionPanelTab): string => {
  switch (tab) {
    case 'visit-notes':
      return 'Visit Summary';
    case 'medical-records':
      return 'Lab Order Results';
    case 'radiology':
      return 'Imaging Results';
    case 'immunization':
      return 'Immunization';
    case 'assessment-forms':
      return 'Assessment Forms';
    case 'internal-referral':
      return 'Internal Referral';
    case 'notes':
      return 'Notes';
    default:
      return 'Panel';
  }
};

const getDateFromVisitKey = (visitKey: string) => {
  const splittedKeys = visitKey?.split('-');
  const datePart = splittedKeys?.length > 1 ? splittedKeys?.[splittedKeys.length - 1]?.trim() : visitKey;
  const parsedDate = datePart ? parse(datePart, 'MM/dd/yyyy hh:mm a', new Date()) : new Date();

  return isValid(parsedDate) ? parsedDate : new Date();
};

// TODO: Fragile method of extracting date from visit key. based on visit or encounter or order datetime should be used
const formatVisitDate = (visitDisplay: string) => {
  const parsedDate = getDateFromVisitKey(visitDisplay);
  return format(parsedDate, 'MMMM d, yyyy. hh:mm a');
};

const getEncounter = (encounter: Encounter, conceptDisplay: string) =>
  encounter?.obs?.find(o => o.concept.display === conceptDisplay);

const getObsValue = (encounter: Encounter, conceptDisplay: string) =>
  encounter?.obs?.find(o => o.concept.display === conceptDisplay)?.value;

const normalizeObsText = (value: unknown): string => {
  if (value == null || value === EMPTY_RESULT) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object' && value !== null && 'display' in value) {
    return ((value as { display?: string }).display ?? '').trim();
  }
  return String(value).trim();
};

const getEncounterAuthor = (encounter: Encounter, fallback = 'Provider'): string =>
  encounter?.encounterProviders?.[0]?.provider?.person?.display ??
  encounter?.encounterProviders?.[0]?.provider?.display ??
  fallback;

const flattenEncountersByType = (visits: Visit[], encounterType: EncounterTypeConfig) => {
  const flattened: Array<{ encounter: Encounter; visit: Visit }> = [];

  for (const visit of visits) {
    for (const encounter of visit?.encounters ?? []) {
      const matchesByUuid = Boolean(encounterType.uuid) && encounter?.encounterType?.uuid === encounterType.uuid;
      const matchesByDisplay = encounter?.encounterType?.display === encounterType.display;

      if (matchesByUuid || matchesByDisplay) {
        flattened.push({ encounter, visit });
      }
    }
  }

  return flattened.sort(
    (a, b) =>
      new Date(b.encounter.encounterDatetime ?? 0).getTime() - new Date(a.encounter.encounterDatetime ?? 0).getTime(),
  );
};

const getObs = (encounter: Encounter, conceptDisplay: string) =>
  encounter?.obs?.find(o => o.concept.display === conceptDisplay);

const formatOrderDate = (orderDate: string) => format(parseISO(orderDate), 'MMM d, yyyy. h.mm a');

const getOrderTimeline = (orders: Order[]) => {
  if (!orders?.length) {
    return {
      startDate: null,
      endDate: null,
      maxDuration: 0,
    };
  }

  let minDate: Date | null = null;
  let maxDate: Date | null = null;
  let maxDuration = 0;

  for (const order of orders) {
    const dateValue = order.scheduledDate ?? order.dateActivated;
    if (!dateValue) continue;

    const date = new Date(dateValue);

    // Min date
    if (!minDate || date < minDate) {
      minDate = date;
    }

    // Max date
    if (!maxDate || date > maxDate) {
      maxDate = date;
    }

    // Duration
    if (order.duration && order.durationUnits?.display) {
      const durationInDays = order.duration * durationUnitToDays[order.durationUnits.display];

      if (durationInDays > maxDuration) {
        maxDuration = durationInDays;
      }
    }
  }

  return {
    startDate: minDate!,
    endDate: maxDate!,
    maxDuration,
  };
};

export {
  getTabTitle,
  getEncounter,
  getObs,
  getObsValue,
  normalizeObsText,
  getEncounterAuthor,
  flattenEncountersByType,
  getDateFromVisitKey,
  formatVisitDate,
  formatOrderDate,
  getOrderTimeline,
};
